import type { IGroup } from '@bridebook/models/source/models/Countries/Groups.types';
import type { ITask } from '@bridebook/models/source/models/Countries/Tasks.types';
import { useCountryGroupsQuery } from '@bridebook/web/lib/ai-planner/hooks/use-country-groups';
import { normaliseBlueprint } from '@bridebook/web/lib/ai-planner/planning/blueprint/normalise';
import { taskNameTranslator } from '@bridebook/web/lib/ai-planner/planning/blueprint/task-names';
import type { PlanningBlueprint } from '@bridebook/web/lib/ai-planner/planning/blueprint/types';
import { ChecklistActionTypes } from '@bridebook/web/lib/checklist/action-types';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo } from 'react';

/**
 * The country checklist template: tasks from the slice the checklist listener fills, groups
 * from a planner-scoped read. The planner can open on any page, so neither is sure to be
 * loaded. Both reads are cache-first, so asking for the tasks costs nothing per turn.
 */
export const usePlanningBlueprint = (): {
  blueprint: PlanningBlueprint | null;
  /**
   * Both sources have arrived. The slice keeps no arrival flag for the template, so the
   * tasks count as arrived once there are any: a market the planner opens in always has
   * a template, and an empty one would fail the app's own checklist set-up the same way.
   * A template that never arrives is told apart from a slow one by the slice's failure.
   */
  settled: boolean;
  /**
   * The groups read came back empty-handed. The template's own failure belongs to the
   * checklist slice, which records it for every reader rather than for this one.
   */
  failed: boolean;
} => {
  const dispatch = useDispatch();
  const countryTasks = useSelector((state) => state.checklist.tasksInitial);
  const country = useSelector((state) => state.weddings.profile.l10n?.country);
  const {
    groups: countryGroups,
    settled: groupsSettled,
    failed: groupsFailed,
  } = useCountryGroupsQuery(country);

  const hasTasks = Object.keys(countryTasks).length > 0;

  useEffect(() => {
    if (!hasTasks) {
      dispatch({ type: ChecklistActionTypes.FETCH_TASKS_INITIAL });
    }
  }, [dispatch, hasTasks]);

  // Task names follow the couple's language, as on the checklist page.
  const { i18n } = useTranslation('countries');
  const language = i18n?.language;
  // The translator reads the active language, so a new language builds a new one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const taskName = useMemo(() => taskNameTranslator(), [language]);

  const blueprint = useMemo(
    () =>
      normaliseBlueprint({
        countryTasks: countryTasks as unknown as Record<string, ITask>,
        countryGroups: (countryGroups ?? {}) as unknown as Record<string, IGroup>,
        country,
        taskName,
      }),
    [countryTasks, countryGroups, country, taskName],
  );
  const settled = hasTasks && groupsSettled;

  return useMemo(
    () => ({ blueprint, settled, failed: groupsFailed }),
    [blueprint, settled, groupsFailed],
  );
};
